import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import {
  Buildings,
  Stack,
  CurrencyEur,
  HouseLine,
} from "@phosphor-icons/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { applyMask } from "shared/utils/masks";
import {
  DashboardBlock,
  DashboardBlockHeader,
  DashboardBlockContent,
} from "../layout/DashboardBlock";
import { BlockLoadingState } from "../states/BlockStates";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

export interface CompanyPortfolioEntry {
  companyId: number | null;
  companyName: string;
  buildingCount: number;
  unitCount: number;
  vacantUnitCount: number;
  totalActualRent: number;
  buildingIds: string[];
}

export interface CompanyPortfolioMetrics {
  distribution: CompanyPortfolioEntry[];
  totals: {
    companies: number;
    buildings: number;
    units: number;
    vacantUnits: number;
    totalActualRent: number;
  };
}

type MetricMode = "buildings" | "units" | "vacant" | "rent";

const MODE_CONFIG: Record<
  MetricMode,
  {
    label: string;
    color: string;
    icon: typeof Buildings;
    formatter: (value: number) => string;
    suffix: string;
  }
> = {
  buildings: {
    label: "Gebouwen",
    color: "#5B7304",
    icon: Buildings,
    formatter: (value) => value.toString(),
    suffix: "gebouwen",
  },
  units: {
    label: "Units",
    color: "#EA8162",
    icon: Stack,
    formatter: (value) => value.toString(),
    suffix: "units",
  },
  vacant: {
    label: "Leegstand",
    color: "#F59E0B",
    icon: HouseLine,
    formatter: (value) => value.toString(),
    suffix: "leegstaande units",
  },
  rent: {
    label: "Huur",
    color: "#3B82F6",
    icon: CurrencyEur,
    formatter: (value) => applyMask(value, "valuta"),
    suffix: "/mnd huur (zelfde definitie als blok Contractuele huur)",
  },
};

const RENT_DEFINITION =
  "Categorie Huur uit het blok Contractuele huur: basishuur (grootboek 700000, 700001, 700002, 700004) van lopende contracten, contracten in opzeg en in concept, aan de prijs die vandaag geldt. Exclusief attributen (garages, staanplaatsen, laadpalen), provisies, provisie OV en overige opbrengsten.";

const MODE_ORDER: MetricMode[] = ["buildings", "units", "vacant", "rent"];

// Grid template aligns header with each row: name flex | 4 numeric cols
const COLUMN_GRID =
  "grid grid-cols-[minmax(0,1fr)_56px_64px_72px_96px] items-center gap-3";

interface CompanyPortfolioBlockNewProps {
  embedded?: boolean;
}

export const CompanyPortfolioBlockNew = ({
  embedded = false,
}: CompanyPortfolioBlockNewProps = {}) => {
  const { session } = useSession();
  const [metrics, setMetrics] = useState<CompanyPortfolioMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mode, setMode] = useState<MetricMode>("buildings");

  useEffect(() => {
    if (!session) return;

    let cancelled = false;
    const fetchMetrics = async () => {
      try {
        setIsLoading(true);
        const response = await fetchWithAuth(
          `${API_URL}/building/company-portfolio-metrics`,
          { method: "GET" },
          session,
        );
        if (!response.ok) {
          throw new Error(
            `Kon vennootschap portfolio metrics niet ophalen (HTTP ${response.status})`,
          );
        }
        const data: CompanyPortfolioMetrics = await response.json();
        if (!cancelled) setMetrics(data);
      } catch (error) {
        console.error("Error fetching company portfolio metrics:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Stable table order — always sorted by building count so rows stay
  // comparable across toggles. The chart below reorders by the active metric.
  const tableDistribution = useMemo(() => {
    if (!metrics) return [];
    return [...metrics.distribution].sort(
      (a, b) => b.buildingCount - a.buildingCount,
    );
  }, [metrics]);

  const chartDistribution = useMemo(() => {
    if (!metrics) return [];
    const pick = (entry: CompanyPortfolioEntry) => {
      if (mode === "buildings") return entry.buildingCount;
      if (mode === "units") return entry.unitCount;
      if (mode === "vacant") return entry.vacantUnitCount;
      return entry.totalActualRent;
    };
    return [...metrics.distribution].sort((a, b) => pick(b) - pick(a));
  }, [metrics, mode]);

  const config = MODE_CONFIG[mode];
  const IconComponent = config.icon;

  const chartData = useMemo(() => {
    if (chartDistribution.length === 0) return null;
    return {
      labels: chartDistribution.map((entry) => entry.companyName),
      datasets: [
        {
          data: chartDistribution.map((entry) => {
            if (mode === "buildings") return entry.buildingCount;
            if (mode === "units") return entry.unitCount;
            if (mode === "vacant") return entry.vacantUnitCount;
            return entry.totalActualRent;
          }),
          backgroundColor: config.color,
          borderRadius: 4,
          barThickness: 14,
        },
      ],
    };
  }, [chartDistribution, mode, config.color]);

  const chartOptions = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: config.color,
          borderWidth: 1,
          padding: 10,
          titleFont: { size: 12 },
          bodyFont: { size: 11 },
          callbacks: {
            label: (context: any) => {
              const raw = context.raw as number;
              return `${config.formatter(raw)} ${config.suffix}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: "rgba(49, 64, 59, 0.08)" },
          ticks: {
            font: { size: 10 },
            callback: (value: any) => {
              if (mode === "rent") {
                const num = Number(value);
                if (num >= 1000) return `€${(num / 1000).toFixed(0)}k`;
                return `€${num}`;
              }
              return value;
            },
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            font: { size: 10 },
            autoSkip: false,
          },
        },
      },
    }),
    [config, mode],
  );

  const bodyContent = (
    <>
      {isLoading ? (
        <BlockLoadingState message="Vennootschap gegevens aan het ophalen..." />
      ) : !metrics || metrics.distribution.length === 0 ? (
        <div className="text-neutral-secondary-mid py-8 text-center text-sm">
          Geen vennootschap gegevens beschikbaar
        </div>
      ) : (
        <div className="space-y-4">
          {/* Toggle */}
          <div className="bg-neutral-primary/5 inline-flex flex-wrap rounded-lg p-1">
            {MODE_ORDER.map((option) => {
              const optionConfig = MODE_CONFIG[option];
              const OptionIcon = optionConfig.icon;
              const isActive = mode === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setMode(option)}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-white text-moss-primary shadow-sm"
                      : "text-neutral-secondary-mid hover:text-neutral-primary"
                  }`}
                >
                  <OptionIcon weight="duotone" size={14} />
                  <span>{optionConfig.label}</span>
                </button>
              );
            })}
          </div>

          {/* Totals summary */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-md border border-neutral-primary/10 bg-white p-2">
              <div className="text-neutral-secondary-mid flex items-center gap-1 text-[10px] uppercase">
                <Buildings weight="duotone" size={12} />
                <span>Gebouwen</span>
              </div>
              <div className="text-foreground text-base font-semibold">
                {metrics.totals.buildings}
              </div>
            </div>
            <div className="rounded-md border border-neutral-primary/10 bg-white p-2">
              <div className="text-neutral-secondary-mid flex items-center gap-1 text-[10px] uppercase">
                <Stack weight="duotone" size={12} />
                <span>Units</span>
              </div>
              <div className="text-foreground text-base font-semibold">
                {metrics.totals.units}
              </div>
            </div>
            <div className="rounded-md border border-neutral-primary/10 bg-white p-2">
              <div className="text-neutral-secondary-mid flex items-center gap-1 text-[10px] uppercase">
                <HouseLine weight="duotone" size={12} />
                <span>Leegstand</span>
              </div>
              <div className="text-foreground text-base font-semibold">
                {metrics.totals.vacantUnits}
              </div>
            </div>
            <div
              className="rounded-md border border-neutral-primary/10 bg-white p-2"
              title={RENT_DEFINITION}
            >
              <div className="text-neutral-secondary-mid flex items-center gap-1 text-[10px] uppercase">
                <CurrencyEur weight="duotone" size={12} />
                <span>Huur</span>
              </div>
              <div className="text-foreground text-base font-semibold">
                {applyMask(metrics.totals.totalActualRent, "valuta")}
              </div>
            </div>
          </div>

          {/* Chart */}
          {chartData && (
            <div
              style={{
                height: Math.max(120, chartDistribution.length * 26),
              }}
              className="w-full"
            >
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}

          {/* Per-company table */}
          <div className="border-t border-neutral-primary/10 pt-3">
            <div
              className={`${COLUMN_GRID} text-neutral-secondary-mid mb-1 px-2 text-[10px] uppercase tracking-wide`}
            >
              <span>Vennootschap</span>
              <span className="text-right">Geb.</span>
              <span className="text-right">Units</span>
              <span className="text-right">Leegstand</span>
              <span className="text-right" title={RENT_DEFINITION}>
                Huur/mnd
              </span>
            </div>
            <div className="divide-y divide-neutral-primary/5">
              {tableDistribution.map((entry) => {
                const vacancyRate =
                  entry.unitCount > 0
                    ? (entry.vacantUnitCount / entry.unitCount) * 100
                    : 0;
                return (
                  <div
                    key={entry.companyId ?? "none"}
                    className={`${COLUMN_GRID} hover:bg-neutral-primary/5 rounded px-2 py-1.5 text-xs`}
                  >
                    <span
                      className="text-foreground truncate font-medium"
                      title={entry.companyName}
                    >
                      {entry.companyName}
                    </span>
                    <span
                      className={`text-right tabular-nums ${
                        mode === "buildings"
                          ? "text-moss-primary font-semibold"
                          : "text-neutral-secondary-mid"
                      }`}
                    >
                      {entry.buildingCount}
                    </span>
                    <span
                      className={`text-right tabular-nums ${
                        mode === "units"
                          ? "text-moss-primary font-semibold"
                          : "text-neutral-secondary-mid"
                      }`}
                    >
                      {entry.unitCount}
                    </span>
                    <span
                      className={`text-right tabular-nums ${
                        mode === "vacant"
                          ? "text-moss-primary font-semibold"
                          : entry.vacantUnitCount > 0
                            ? "text-neutral-primary"
                            : "text-neutral-secondary-mid"
                      }`}
                      title={`${vacancyRate.toFixed(0)}% leegstand`}
                    >
                      {entry.vacantUnitCount}
                      {entry.unitCount > 0 && (
                        <span className="text-neutral-secondary-mid ml-1 text-[10px]">
                          ({vacancyRate.toFixed(0)}%)
                        </span>
                      )}
                    </span>
                    <span
                      className={`text-right tabular-nums ${
                        mode === "rent"
                          ? "text-moss-primary font-semibold"
                          : "text-neutral-secondary-mid"
                      }`}
                    >
                      {applyMask(entry.totalActualRent, "valuta")}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-neutral-secondary-mid mt-2 px-2 text-[10px] italic leading-relaxed">
              Huur = de categorie Huur uit het blok Contractuele huur (zelfde
              contracten, prijzen en indeling, ook per vennootschap). Attributen,
              provisies, provisie OV en overige opbrengsten staan daar apart.
            </p>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return bodyContent;
  }

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<Buildings weight="duotone" size={20} />}
        title="Statistieken per vennootschap"
        action={
          <span className="text-neutral-secondary-mid flex items-center gap-1 text-xs">
            <IconComponent weight="duotone" size={14} />
            {config.label}
          </span>
        }
      />
      <DashboardBlockContent padding="md">{bodyContent}</DashboardBlockContent>
    </DashboardBlock>
  );
};
